import {
  Alert,
  AlertTitle,
  Button,
  HStack,
  Image,
  Input,
  Spacer,
  Spinner,
  Stack,
  Text,
  Link,
  Flex,
  Tooltip
} from '@chakra-ui/react';
import { Controller, useFormContext } from 'react-hook-form';
import { Link as ReactRouterLink } from 'react-router-dom';
import { useState } from 'react';

import { useInitiativeOptions } from '@services/query-services/initiatives.query-service';
import { SlackLogo } from '@assets/images';
import { SFInfoIcon } from '@assets/icons';
import CustomSelect from '@components/general/form/custom-select.component';
import HorizontalRadio from '@components/general/form/horizontal-radio.component';
import InfoBlurb from '@components/general/info-blurb.component';
import StepWithLabelComponent from '@components/general/form/step-with-label.component';

const cadenceOptions = [
  // { label: 'Just Once', value: 'once', description: 'Single Summary' },
  { label: 'Daily', value: 'daily', description: 'At Midnight' },
  { label: 'Weekly', value: 'weekly', description: 'Every Sunday' },
  { label: 'Monthly', value: 'monthly', description: 'Last Day of Month' }
];

/**
 *
 * @param {{
 *  onSubmit: (channelBody: Partial<Channel>) => void
 *  sourceType: 'Slack' | 'Teams'
 *  isSubmitting: boolean
 *  children: JSX.Element
 * }} CreateDigestFormProps
 * @returns
 */
export default function DigestForm({
  isEdit,
  onSubmit,
  parentDisclosure,
  sourceType,
  isSubmitting,
  children
}) {
  const isSlack = sourceType === 'Slack';
  const [showAboutBlurb, setShowAboutBlurb] = useState(isSlack);
  const { data: focusOptions } = useInitiativeOptions({ type: 'Focus' });
  const {
    handleSubmit,
    register,
    control,
    getFieldState,
    formState: { isValid, isSubmitted }
  } = useFormContext();

  return (
    <Stack
      as='form'
      data-testid='create-digest-form'
      onSubmit={handleSubmit(onSubmit)}
      p={{ base: 4, lg: 6 }}
      gap={6}
    >
      <HStack justify='space-between'>
        <Text variant='body-700-md'>
          Summarize one or multiple {sourceType} channels at once as a digest.
        </Text>
        {!isEdit && (
          <Button
            variant='reskinWhite'
            size='reskinMd'
            as={ReactRouterLink}
            to='/settings/manage-digests'
            onClick={parentDisclosure.onClose}
          >
            Manage Digests
          </Button>
        )}
      </HStack>
      {showAboutBlurb && (
        <InfoBlurb
          title='About Slack Integration'
          icon={
            <Image
              src={SlackLogo}
              p='9px'
            />
          }
          iconWrapperProps={{ bgColor: 'surface.containerLowest', borderRadius: 'xl' }}
          onCloseClick={() => setShowAboutBlurb(false)}
        >
          Make sure the FORA Slack integration is added in your Slack channels. Based on
          organization rules, private channels cannot be summarized.&nbsp;
          <Link
            textDecor='underline'
            href='https://intercom.help/fora/en/articles/11145895-fora-slack-digest-configuration'
            target='_blank'
          >
            Learn more
          </Link>
        </InfoBlurb>
      )}
      {children}
      <Controller
        control={control}
        name='cadenceInterval'
        render={({ field: { name, value, onChange }, fieldState }) => (
          <StepWithLabelComponent
            label={`${sourceType} Digest Frequency`}
            labelChildren={
              <Tooltip
                minW='436px'
                pl={5}
                py={1}
                borderRadius='lg'
                hasArrow
                label={
                  <ul>
                    <li>Daily – Repeats every 24 hours at midnight.</li>
                    <li>Weekly – Repeats every Sunday at midnight.</li>
                    <li>Monthly – Repeats on the last day of each month at midnight.</li>
                  </ul>
                }
              >
                <Flex
                  as='span'
                  cursor='pointer'
                >
                  <SFInfoIcon color='surface.onSurfaceVariant' />
                </Flex>
              </Tooltip>
            }
            name={name}
            fieldState={fieldState}
          >
            <HorizontalRadio
              options={cadenceOptions}
              value={value}
              control={control}
              onChange={onChange}
            />
          </StepWithLabelComponent>
        )}
      />
      <StepWithLabelComponent
        label={`${sourceType} Digest Title`}
        name='name'
        fieldState={getFieldState('name')}
      >
        <Input
          placeholder={`Your ${sourceType} Digest Title`}
          {...register('name', { required: 'Add a Title' })}
        />
      </StepWithLabelComponent>

      <Controller
        control={control}
        name='initiative'
        rules={{ required: 'Category is Required' }}
        render={({ field: { name, value, onChange }, fieldState }) => (
          <StepWithLabelComponent
            label='Category'
            name={name}
            fieldState={fieldState}
          >
            <CustomSelect
              size='md'
              isMulti={false}
              placeholder='Choose Category'
              options={focusOptions}
              value={value}
              onChange={onChange}
              menuPlacement='auto'
            />
          </StepWithLabelComponent>
        )}
      />
      <HStack gap={6}>
        {!isValid && isSubmitted && (
          <Alert
            h={10}
            status='error'
          >
            <AlertTitle>Please resolve the required fields</AlertTitle>
          </Alert>
        )}
        <Spacer />
        <Button
          type='submit'
          alignSelf='flex-end'
          size='reskinLg'
          isDisabled={isSubmitting}
        >
          <Text as='span'>
            {isEdit ? 'Save' : 'Create'} {sourceType} Digest
          </Text>
          {isSubmitting && (
            <Spinner
              size='xs'
              color='brandPrimary.onPrimary'
              ml={2}
            />
          )}
        </Button>
      </HStack>
    </Stack>
  );
}
